import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { OrderTableRowData } from './OrderTable';
import { getISOWeek, getISOWeekYear, startOfWeek } from 'date-fns';
import type {
  Currency,
  PaymentUnit,
  VoyageCharterForm_AssortmentFragment,
} from '../../graphql';
import { useVoyageCharterFormQuery } from '../../graphql';
import { useCurrentUser } from '../../state/zustand';
import { nodesToOptions } from '../../lib/form-util';
import { reducedUnits } from '../../constants/cargoUnits';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import { Box, Text, Tooltip } from '../ui';
import type { CellProps, Row, TableInstance } from 'react-table';
import type { TCScheduleEntry } from '../Scenario';
import {
  isBackupScenario,
  isInitialScenario,
  isMasterScenario,
} from '../Scenario';
import type { ScenarioSchedule } from '@seaber/common/core/scenario';
import { DefaultMap } from '@seaber/common/lib/collections';
import type { Virtualizer } from '@tanstack/react-virtual';
import useEvent from '../hooks/useEvent';

export type IncomingContractType =
  | 'INQUIRY'
  | 'SPOT'
  | 'COA'
  | 'PRELIMINARY'
  | 'ON_SUBS'
  | 'SUBS_EXPIRED';

export type OrderStatus =
  | 'completed'
  | 'assigned'
  | 'unassigned'
  | 'committed'
  | 'shared'
  | 'spot'
  | 'spotCommitted'
  | 'spotShared'
  | 'coa'
  | 'coaCommitted'
  | 'coaShared';

export type RowInputState = {
  assortmentId: number | null;
  cancelling: string | null;
  cargoAmount: number | string | null;
  cargoUnitId: number | null;
  chartererId: number | null;
  currency: Currency | null;
  deliveredBefore: string | null;
  dischargePortId: number | null;
  extraInfo: string | null;
  freightAmount: number | string | null;
  freightUnit: PaymentUnit | null;
  layday: string | null;
  loadPortId: number | null;
  orderName: string | null;
  rta: string | null;
};

/**
 * Returns the options for the select inputs in the order table
 */
export function useVCDataToOptions() {
  const currentUser = useCurrentUser();
  const shouldUseReducedUnits =
    currentUser?.rootOrganization?.configReducedUnits ?? false;

  const { data } = useVoyageCharterFormQuery();

  const assortments = useMemo(
    () => nodesToOptions(data?.allAssortments?.nodes ?? []),
    [data?.allAssortments?.nodes],
  );

  const charterers = useMemo(
    () => nodesToOptions(data?.charterers?.nodes ?? []),
    [data?.charterers?.nodes],
  );

  const cargoUnits = useMemo(
    () =>
      nodesToOptions(
        data?.allCargoUnits?.nodes.filter((unit) =>
          shouldUseReducedUnits
            ? reducedUnits.includes(unit?.name ?? '')
            : unit,
        ) ?? [],
      ),
    [data?.allCargoUnits?.nodes, shouldUseReducedUnits],
  );

  return { assortments, charterers, cargoUnits, data };
}

export function isAssortmentValid(
  a: VoyageCharterForm_AssortmentFragment | null | undefined,
): boolean {
  return !!(
    a &&
    (a.assortmentGroup ||
      a.specificGravity ||
      a.stowageFactorMin ||
      a.stowageFactorMax ||
      a.cargoType ||
      a.marginUnit ||
      a.loadingUnit ||
      a.dischargingUnit)
  );
}

/**
 * Returns the voyage charter id of the order
 * @param row
 */
export function getRowIncomingVoyageCharterId(
  row: Row<OrderTableRowData>,
): number | null {
  return row.original.incomingVoyageCharterRelation?.voyageCharterId ?? null;
}

/**
 * Returns the difference between the initial values and the current state with initial values as the base
 * @param initialValues
 * @param currentState
 */
export function getStateDifference(
  initialValues: RowInputState,
  currentState: Partial<RowInputState>,
): Partial<RowInputState> {
  return Object.entries(initialValues).reduce((acc, cur) => {
    const [key, value] = cur as [
      keyof RowInputState,
      RowInputState[keyof RowInputState],
    ];
    if (value !== currentState?.[key] && currentState?.[key] !== undefined) {
      return { ...acc, [key]: initialValues[key] };
    }
    return acc;
  }, {});
}

/**
 * Returns the contract type of the order
 * @param order
 */
export function getOrderContractType(
  order: ScenarioOrder,
): IncomingContractType {
  const incomingVc =
    order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId ?? null;

  if (!incomingVc || incomingVc.isInquiry) {
    return 'INQUIRY';
  }

  const now = new Date();
  const onSubsUntil = incomingVc.onSubsUntil
    ? new Date(incomingVc.onSubsUntil)
    : null;

  if (onSubsUntil !== null) {
    if (onSubsUntil <= now) {
      return 'SUBS_EXPIRED';
    }

    return 'ON_SUBS';
  }

  const coaId =
    incomingVc.voyageCharterToContractsByVoyageCharterId.nodes[0]?.coaId ??
    null;

  if (coaId === null) {
    return 'SPOT';
  }

  if (incomingVc.isPreliminary) {
    return 'PRELIMINARY';
  } else {
    return 'COA';
  }
}

/**
 * Returns the status of the order
 * @param orderId
 * @param scenario
 * @param scheduleByOrderId
 * @param completedOrderIds
 */
export function getOrderStatus(
  orderId: ScenarioOrder['id'],
  scenario: ScenarioSchedule,
  scheduleByOrderId: Map<number | string, TCScheduleEntry>,
  completedOrderIds: Set<number>,
): OrderStatus {
  if (completedOrderIds.has(orderId)) {
    return 'completed';
  }
  const scheduleEntry = scheduleByOrderId.get(orderId);
  if (scheduleEntry?.shared) {
    return 'shared';
  } else {
    const isNotUserScenario =
      isMasterScenario(scenario) ||
      isBackupScenario(scenario) ||
      isInitialScenario(scenario);
    if (
      scheduleEntry &&
      (isNotUserScenario || !scenario.entryStates.has(scheduleEntry.clientId))
    ) {
      return 'committed';
    } else if (scheduleEntry) {
      return 'assigned';
    } else {
      const spot = scenario.spotAssignments.find(
        (spot) => spot.orderId === orderId,
      );
      if (spot?.shared) {
        return 'spotShared';
      } else if (
        spot &&
        (isNotUserScenario || !scenario.entryStates.has(spot.clientId))
      ) {
        return 'spotCommitted';
      } else if (spot) {
        return 'spot';
      } else {
        const coaAssignment = scenario.coaAssignments.find(
          (coaAssignment) => coaAssignment.orderId === orderId,
        );
        if (coaAssignment?.shared) {
          return 'coaShared';
        } else if (
          coaAssignment &&
          (isNotUserScenario ||
            !scenario.entryStates.has(coaAssignment.clientId))
        ) {
          return 'coaCommitted';
        } else if (coaAssignment) {
          return 'coa';
        } else {
          return 'unassigned';
        }
      }
    }
  }
}

/**
 * Returns the start of the week of the layday
 */
export const laydayToISOWeek = new DefaultMap<string, number | null>((layday) =>
  startOfWeek(new Date(layday), { weekStartsOn: 1 }).getTime(),
);

/**
 * Returns the formatted date
 * @param showTimes
 */
export function timeFormat(showTimes?: boolean): string {
  return showTimes ? 'D.M.yyyy HH:mm (z)' : 'D.M.yyyy';
}

/**
 * Returns sorted rows by date
 * @param rowA
 * @param rowB
 * @param field
 * @param isDescending
 */
export function sortDateType(
  rowA: Row<OrderTableRowData>,
  rowB: Row<OrderTableRowData>,
  field: keyof Pick<
    OrderTableRowData,
    'layday' | 'cancelling' | 'rta' | 'deliveredBefore'
  >,
  isDescending: boolean,
) {
  if (rowA.isGrouped || rowB.isGrouped) return 0;

  const vRowA = rowA.original[field];
  const vRowB = rowB.original[field];
  const aTime = vRowA
    ? new Date(vRowA).getTime()
    : isDescending
      ? -Infinity
      : Infinity;
  const bTime = vRowB
    ? new Date(vRowB).getTime()
    : isDescending
      ? -Infinity
      : Infinity;
  return aTime === bTime ? 0 : aTime > bTime ? 1 : -1;
}

/**
 * Returns sorted rows by location's name
 * @param rowA
 * @param rowB
 * @param field
 */
export function sortLocationType(
  rowA: Row<OrderTableRowData>,
  rowB: Row<OrderTableRowData>,
  field: keyof Pick<
    OrderTableRowData,
    'locationByLoadPortId' | 'locationByDischargePortId'
  >,
) {
  const a = rowA.original?.[field]?.name ?? '';
  const b = rowB.original?.[field]?.name ?? '';
  return a.localeCompare(b);
}

/**
 * Expand all rows that have a week indicator
 * @param instance
 */
export const useExpandByWeek = <D extends object>(
  instance: TableInstance<D>,
): void => {
  const { rows, toggleRowExpanded } = instance;

  useEffect(() => {
    rows.forEach((row) => {
      const id = row.id;
      if (id.includes('week')) {
        toggleRowExpanded([id], true);
      }
    });
  }, [toggleRowExpanded, rows]);
};

/**
 * Reset the sort by to week if it is empty
 * @param instance
 */
export const useResetSortBy = <D extends object>(
  instance: TableInstance<D>,
): void => {
  const { toggleSortBy, state } = instance;
  useEffect(() => {
    if (state.sortBy.length === 0) {
      toggleSortBy('week');
    }
  }, [state.sortBy, toggleSortBy]);
};

export function getColumnWidth(
  orders: OrderTableRowData[],
  columnId: 'orderName' | 'externalOrderId' | 'remarks',
): number {
  switch (columnId) {
    case 'externalOrderId':
      return orders.some((i) => (i?.externalOrderId ?? '').length > 20)
        ? 200
        : 120;
    case 'orderName':
      return orders.some(
        (i) =>
          (i?.orderDetailsByOrderId?.nodes.at(0)?.orderName ?? '').length > 20,
      )
        ? 200
        : 120;
    case 'remarks':
      return orders.some(
        (i) =>
          (i?.orderDetailsByOrderId?.nodes.at(0)?.extraInfo ?? '').length > 20,
      )
        ? 250
        : 200;
    default:
      return 150;
  }
}

/**
 * Returns the initial values of the order based on row state
 * @param order
 */
export function getRowInitialValues(order: OrderTableRowData): RowInputState {
  const orderDetails = order.orderDetailsByOrderId.nodes.at(0);
  const {
    voyageCharterOrderFreightsByOrderVoyageCharterRelationId: vcFreights,
    voyageCharterByVoyageCharterId: vc,
  } = order.incomingVoyageCharterRelation || {};
  const vcFreight = vcFreights?.nodes.at(0);

  const cargoAmount = order?.cargoAmount ? Number(order.cargoAmount) : null;
  const freightAmount = vcFreight?.freightAmount
    ? Number(vcFreight.freightAmount)
    : null;

  return {
    assortmentId: order.assortmentByAssortmentId?.id ?? null,
    cancelling: order.cancelling ?? null,
    cargoAmount,
    cargoUnitId: order.cargoUnitByCargoUnitId?.id ?? null,
    chartererId: order.organizationByChartererOrganizationId?.id ?? null,
    currency: vc?.currency ?? null,
    deliveredBefore: order.deliveredBefore ?? null,
    dischargePortId: order.locationByDischargePortId?.id ?? null,
    extraInfo: orderDetails?.extraInfo ?? null,
    freightAmount,
    freightUnit: vcFreight?.freightUnit ?? null,
    layday: order.layday ?? null,
    loadPortId: order.locationByLoadPortId?.id ?? null,
    orderName: orderDetails?.orderName ?? null,
    rta: order.rta ?? null,
  };
}

/**
 * React Components ------------------------------------------------------------
 */

export function LocationCell({
  value,
}: {
  value: ScenarioLocation | null | undefined;
}): React.ReactElement {
  const name = value?.name ?? '';
  const missingCoordinates = !value?.longitude || !value?.latitude;
  return (
    <div style={{ width: 'fit-content' }}>
      {missingCoordinates ? (
        <Tooltip content={<Text>Missing coordinates!</Text>}>
          <Text minWidth={100} display="block" color="#9d0300">
            {name}
          </Text>
        </Tooltip>
      ) : (
        <Text display="block" isUnselectable>
          {name}
        </Text>
      )}
    </div>
  );
}

export function NotFoundOrder({
  isSearching,
  height,
}: {
  isSearching: boolean;
  height: number;
}): React.ReactElement {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', () => handleResize());
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const noDataText = isSearching
    ? 'No values found matching the search criteria'
    : 'No records found';

  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        width,
        height,
      }}
    >
      <Text fontSize="1em">{noDataText}</Text>
    </div>
  );
}

export function TextBlock(props: {
  value: string | number;
  isInvalid?: boolean;
}): React.ReactElement {
  return (
    <Text
      isUnselectable
      style={{
        display: 'block',
        width: 'fit-content',
        color: props.isInvalid ? '#9d0300' : undefined,
      }}
    >
      {props.value}
    </Text>
  );
}

export function TextCell(
  props: CellProps<OrderTableRowData, string | number>,
): React.ReactElement {
  return <TextBlock value={props.value} />;
}

export const WEEK_INDICATOR_HEIGHT = 28;
export function WeekIndicatorRow({
  columnsWidth,
  startOfWeek,
}: {
  startOfWeek: number | null;
  columnsWidth: number;
}): React.ReactElement {
  const date = startOfWeek ? new Date(startOfWeek) : null;

  return (
    <Box
      height={WEEK_INDICATOR_HEIGHT}
      width={columnsWidth}
      display="flex"
      flex="1 0 auto"
      bg="neutral.gray_150"
      px={3}
      alignItems="center"
    >
      <Text fontWeight="bold" isUnselectable>
        {date === null
          ? `Uncategorized`
          : `Week ${getISOWeek(date)} / ${getISOWeekYear(date)}`}
      </Text>
    </Box>
  );
}

interface UseScrollToOrderParams {
  rowsToRender: Row<OrderTableRowData, unknown>[];
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

interface ScrollToOrderHandle {
  scrollToOrderId: (orderId: number) => void;
}

export function useScrollToOrder({
  rowsToRender,
  rowVirtualizer,
}: UseScrollToOrderParams): ScrollToOrderHandle {
  const pendingScrollToOrderId = useRef<number | null>(null);

  const findRowToScrollTo = useEvent((orderId?: number) =>
    rowsToRender.findIndex((row) => row.original?.id === orderId),
  );

  const scrollToOrderId = (orderId: number) => {
    const index = findRowToScrollTo(orderId);
    if (index === -1) {
      pendingScrollToOrderId.current = orderId;
    } else {
      rowVirtualizer.scrollToIndex(index, { align: 'center' });
    }
  };

  // when we get new rows to the orders table, check if we have a pending row to scroll to
  // the scroll intent is fired when the orders table might not yet have the new order we
  // want to scroll to
  useEffect(() => {
    if (pendingScrollToOrderId.current !== null) {
      const index = findRowToScrollTo(pendingScrollToOrderId.current);

      if (index !== -1) {
        rowVirtualizer.scrollToIndex(index, { align: 'center' });
        pendingScrollToOrderId.current = null;
      }
    }
  }, [rowsToRender, rowVirtualizer, findRowToScrollTo]);

  return { scrollToOrderId };
}
